// TaxPage — operator's year-end tax summary + Tally CSV + PDF download.

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { orpc } from '../lib/orpc.js';
import { format_inr } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';

interface TaxSummary {
  fy_label: string;
  commission_earned_paise: number;
  total_collected_paise: number;
  /** Realized TDS — what the operator actually owes today (from payouts
   *  that have been disbursed). */
  total_tds_paise: number;
  /** Forecast TDS — projection for every scheduled payout in the FY,
   *  including ones not yet disbursed. Backwards-compat: optional. */
  total_tds_forecast_paise?: number;
  monthly: Array<{ due_month: string; collected_paise: number; commission_paise: number }>;
  tds_events: Array<{
    chit_name_en: string;
    slot: number;
    payout_month: string;
    payee_name_en: string;
    chit_value_paise: number;
    tds_rate_pct: number;
    tds_paise: number;
    net_payout_paise: number;
    applies: boolean;
    /** True if the payout has actually been disbursed (recorded in
     *  payout_ledger). False = forecast row. Optional for backwards-compat
     *  with cached older responses. */
    is_realized?: boolean;
  }>;
  config: { tds_rate_pct: number; tds_threshold_paise: number };
}

function current_fy_start(): number {
  // Indian FY: starts April 1. If we're in Jan-Mar, the FY started last year.
  const now = new Date();
  return now.getUTCMonth() >= 3 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
}

export function TaxPage(): ReactElement {
  const { t } = useTranslation();
  const [fy_start_year, set_fy] = useState(current_fy_start());

  const q = useQuery({
    queryKey: ['tax-summary', fy_start_year],
    queryFn: () => orpc.tax.annual_summary({ fy_start_year }),
  });

  const csv_m = useMutation({
    mutationFn: () => orpc.tax.tally_csv({ fy_start_year }),
    onSuccess: (r) => {
      const blob = new Blob([(r as { csv_text: string }).csv_text], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chitos-tax-${fy_start_year}-${(fy_start_year + 1) % 100}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  });

  const pdf_m = useMutation({
    mutationFn: () => orpc.tax.pdf({ fy_start_year }),
    onSuccess: (r) => {
      window.open((r as { pdf_url: string }).pdf_url, '_blank');
    },
  });

  const s = q.data as TaxSummary | undefined;
  const fy_options = useMemo(() => {
    const cur = current_fy_start();
    return [cur, cur - 1, cur - 2];
  }, []);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium">{t('tax.title', 'Tax filing assist')}</h1>
          <p className="text-xs text-ink/60 mt-1">
            {t(
              'tax.subtitle',
              'Indian FY (April–March) summary with TDS computation + Tally-compatible CSV export.',
            )}
          </p>
        </div>
        <select
          value={fy_start_year}
          onChange={(e) => set_fy(Number(e.target.value))}
          className="px-3 py-1.5 border border-line rounded text-sm bg-surface"
        >
          {fy_options.map((y) => (
            <option key={y} value={y}>
              FY {y}-{(y + 1) % 100}
            </option>
          ))}
        </select>
      </div>

      {q.isLoading && <div className="text-sm text-ink/60">Loading…</div>}
      {q.error && <div className="text-sm text-bad">{friendly_error(q.error)}</div>}

      {s && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <Tile
              label={t('tax.total_collected', 'Total collected')}
              paise={s.total_collected_paise}
            />
            <Tile
              label={t('tax.commission', 'Commission income (taxable)')}
              paise={s.commission_earned_paise}
              tone="primary"
            />
            <Tile
              label={
                s.total_tds_forecast_paise !== undefined &&
                s.total_tds_forecast_paise !== s.total_tds_paise
                  ? t('tax.tds_owed_now', 'TDS owed (so far)')
                  : t('tax.total_tds', 'Total TDS')
              }
              paise={s.total_tds_paise}
              tone="bad"
              sub={
                s.total_tds_forecast_paise !== undefined &&
                s.total_tds_forecast_paise !== s.total_tds_paise
                  ? `${t('tax.fy_forecast', 'FY forecast')} ${format_inr(s.total_tds_forecast_paise)}`
                  : undefined
              }
            />
          </div>

          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => csv_m.mutate()}
              disabled={csv_m.isPending}
              className="border border-line bg-surface hover:bg-bg-elev text-ink/80 px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            >
              {csv_m.isPending ? '…' : t('tax.download_csv', 'Download Tally CSV')}
            </button>
            <button
              type="button"
              onClick={() => pdf_m.mutate()}
              disabled={pdf_m.isPending}
              className="bg-primary hover:bg-primary-2 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50"
            >
              {pdf_m.isPending ? '…' : t('tax.generate_pdf', 'Generate PDF')}
            </button>
          </div>

          <section className="mb-6">
            <h2 className="text-sm font-medium text-ink/70 mb-2">
              {t('tax.monthly_breakdown', 'Monthly breakdown')}
            </h2>
            <div className="bg-surface border border-line rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-bg-elev border-b border-line text-ink/60 text-xs">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Month</th>
                    <th className="text-right px-4 py-2 font-medium">Collected</th>
                    <th className="text-right px-4 py-2 font-medium">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {s.monthly.map((m) => (
                    <tr key={m.due_month} className="border-b border-line/40 last:border-b-0">
                      <td className="px-4 py-1.5 font-mono">{m.due_month}</td>
                      <td className="px-4 py-1.5 text-right font-mono">
                        {format_inr(m.collected_paise)}
                      </td>
                      <td className="px-4 py-1.5 text-right font-mono text-primary">
                        {format_inr(m.commission_paise)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {s.tds_events.filter((e) => e.applies).length > 0 && (
            <section>
              <h2 className="text-sm font-medium text-ink/70 mb-2">
                {t('tax.tds_events', 'TDS-applicable payouts')}{' '}
                <span className="text-[11px] text-ink/50">
                  ({s.config.tds_rate_pct}% above {format_inr(s.config.tds_threshold_paise)})
                </span>
              </h2>
              <div className="bg-surface border border-line rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-bg-elev border-b border-line text-ink/60 text-xs">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Month</th>
                      <th className="text-left px-4 py-2 font-medium">Chit</th>
                      <th className="text-right px-4 py-2 font-medium">Slot</th>
                      <th className="text-left px-4 py-2 font-medium">Payee</th>
                      <th className="text-right px-4 py-2 font-medium">Gross</th>
                      <th className="text-right px-4 py-2 font-medium">TDS</th>
                      <th className="text-right px-4 py-2 font-medium">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.tds_events
                      .filter((e) => e.applies)
                      .map((e, i) => {
                        // is_realized is optional for back-compat with older
                        // backend responses; treat missing as forecast (the
                        // safer reading — we don't double-count "owed today"
                        // numbers from old cached responses).
                        const realized = e.is_realized === true;
                        return (
                          <tr
                            key={i}
                            className={
                              'border-b border-line/40 last:border-b-0' +
                              (realized ? '' : ' opacity-70')
                            }
                          >
                            <td className="px-4 py-1.5 font-mono text-xs">
                              {e.payout_month}
                              {!realized && (
                                <span
                                  className="ml-1.5 text-[9px] uppercase tracking-wider px-1 py-px rounded bg-bg-elev text-ink/50"
                                  title="Forecast — payout not yet disbursed"
                                >
                                  {t('tax.forecast', 'forecast')}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-1.5">{e.chit_name_en}</td>
                            <td className="px-4 py-1.5 text-right font-mono">{e.slot}</td>
                            <td className="px-4 py-1.5">{e.payee_name_en}</td>
                            <td className="px-4 py-1.5 text-right font-mono">
                              {format_inr(e.chit_value_paise)}
                            </td>
                            <td className="px-4 py-1.5 text-right font-mono text-bad">
                              {format_inr(e.tds_paise)}
                            </td>
                            <td className="px-4 py-1.5 text-right font-mono">
                              {format_inr(e.net_payout_paise)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Tile({
  label,
  paise,
  tone,
  sub,
}: {
  label: string;
  paise: number;
  tone?: 'primary' | 'bad';
  /** Optional secondary line — used for "FY forecast ₹X" under TDS owed. */
  sub?: string;
}): ReactElement {
  const cls =
    tone === 'primary' ? 'text-primary' : tone === 'bad' ? 'text-bad' : 'text-ink';
  return (
    <div className="bg-surface border border-line rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-ink/50">{label}</div>
      <div className={`text-2xl font-mono mt-1 ${cls}`}>{format_inr(paise)}</div>
      {sub && <div className="text-[11px] text-ink/50 mt-1 font-mono">{sub}</div>}
    </div>
  );
}
